import { useCallback, useRef, useState, type Dispatch } from 'react';

import { useLatestRef } from '@heidi/utils';

import {
  hasIdentityEcho,
  mintTurnId,
  type AcceptedTurn,
  type Message,
  type SubmitTurnResponse,
  type TurnOperation,
} from '../../contracts';
import {
  acceptOptimisticTurn,
  aliasOptimisticTurn,
  ingestOptimisticTurn,
  withdrawOptimisticTurn,
} from '../harness/transcript-store';
import type { TurnLifecycleAction } from '../harness/turn-lifecycle';
import {
  createTurnOperationRegistry,
  type NamedTurnTerminalKind,
} from '../harness/turn-operation-registry';
import type { ApplyToStore } from './useTranscriptStoreState';

/**
 * One optimistic submit, settled when its POST resolves. A stale handle (newer
 * submit, run change, turn boundary) no-ops apart from `accept`, which still
 * settles its own optimistic copy.
 */
export interface TurnSubmissionHandle {
  /** Send as `SubmitTurnRequest.turn_id` so the backend adopts it. */
  turnId: string;
  /**
   * The backend has the turn: claims it as this client's (only a claimed turn's
   * pre-output cancel restores the prompt — see `onTurnDiscarded`) and wakes a
   * backed-off follow stream. Pass the POST response so an identity echo can
   * alias the optimistic messages onto their artifact ids.
   */
  accept: (response?: SubmitTurnResponse | null) => void;
  /**
   * The POST never reached the backend: withdraws the optimistic message,
   * releases the claim, and returns the turn phase to idle.
   */
  reject: (error?: unknown) => void;
  /** Terminal-aware handle registered before any upload or POST. */
  operation: TurnOperation;
}

export interface TurnOperationHandle extends TurnSubmissionHandle {
  /** Paint the optimistic bubble and enter the submitting phase. Idempotent. */
  ingestOptimistic: (messages: Message[]) => void;
}

export interface UseTurnSubmissionParams {
  agentRunId: string | undefined;
  follow: boolean;
  applyToStore: ApplyToStore;
  refetch: () => Promise<unknown>;
  dispatchTurn: Dispatch<TurnLifecycleAction>;
}

export interface TurnSubmissionController {
  handleTurnSubmitted: (
    messages: Message[],
    options?: { turnId?: string }
  ) => TurnSubmissionHandle;
  /**
   * Register terminal completion before any upload/permission await or POST.
   * Does not ingest or throw.
   */
  beginTurnOperation: (options?: { turnId?: string }) => TurnOperationHandle;
  settleNamedTerminal: (turnId: string, kind: NamedTurnTerminalKind) => void;
  settleUnnamedTerminal: () => void;
  abandonOutstandingOperations: (reason: 'run_changed' | 'unmounted') => void;
  /** Re-keys the follow stream; bumped only by an accepted local submit. */
  resubscribeToken: number;
  /** Whether the turn in flight is one THIS client submitted. */
  isLocalTurnPending: () => boolean;
  /** Release the claim and stamp outstanding handles stale (run change, boundary). */
  resetTurnTracking: () => void;
}

const toAcceptedTurn = (
  clientTurnId: string,
  response?: SubmitTurnResponse | null
): AcceptedTurn => ({
  turnId: response?.turn_id ?? clientTurnId,
  ...(response?.user_message_ids && { userMessageIds: response.user_message_ids }),
});

export const useTurnSubmission = ({
  agentRunId,
  follow,
  applyToStore,
  refetch,
  dispatchTurn,
}: UseTurnSubmissionParams): TurnSubmissionController => {
  // Follow mode holds ONE long-lived stream, so `enabled` doesn't toggle on a
  // local submit the way per-turn mode's does. If that stream is mid-backoff
  // (e.g. after an idle infra close), a local submit alone wouldn't wake it —
  // the user would see only the optimistic bubble until the retry delay elapses.
  // Bumping this on a local submit re-keys the stream so it aborts the backoff
  // and opens a fresh connection at once. Remote turns do NOT bump it, so the
  // stream still holds open across those.
  const [resubscribeToken, setResubscribeToken] = useState(0);
  // True while a turn THIS client submitted is in flight (set on submit, reset
  // on every boundary / run change). Gates `onTurnDiscarded` so a pre-output
  // cancel of a turn we did not dispatch (a scheduler or another tab on the
  // same run) never restores our stale last prompt into the composer.
  const localTurnPendingRef = useRef(false);
  // Identifies the submission being tracked. Bumped on every submit, on a run
  // change, and at every turn boundary, so a `TurnSubmissionHandle` whose POST
  // resolves late can tell the turn it describes is gone rather than reach into
  // whatever replaced it.
  const submissionIdRef = useRef(0);
  // Render-phase, unlike the submission id: a POST resolving between the
  // run-change render and that id's effect would otherwise read as current.
  const currentRunIdRef = useLatestRef(agentRunId);
  const registryRef = useRef(createTurnOperationRegistry());

  const isLocalTurnPending = useCallback(() => localTurnPendingRef.current, []);

  const resetTurnTracking = useCallback(() => {
    localTurnPendingRef.current = false;
    submissionIdRef.current += 1;
  }, []);

  const settleNamedTerminal = useCallback(
    (turnId: string, kind: NamedTurnTerminalKind) => {
      registryRef.current.settleNamed(turnId, kind);
    },
    []
  );

  const settleUnnamedTerminal = useCallback(() => {
    registryRef.current.settleUnnamed();
  }, []);

  const abandonOutstandingOperations = useCallback(
    (reason: 'run_changed' | 'unmounted') => {
      registryRef.current.abandonOutstanding(reason);
    },
    []
  );

  const beginTurnOperation = useCallback(
    (submitOptions?: { turnId?: string }): TurnOperationHandle => {
      const submissionId = (submissionIdRef.current += 1);
      const submittedRunId = currentRunIdRef.current;
      const isStale = () =>
        submissionIdRef.current !== submissionId ||
        currentRunIdRef.current !== submittedRunId;
      const turnId = submitOptions?.turnId ?? mintTurnId();
      const operation = registryRef.current.begin(turnId);
      let ingestedMessages: Message[] | null = null;

      return {
        turnId,
        operation,
        ingestOptimistic: (messages) => {
          if (ingestedMessages !== null) return;
          ingestedMessages = messages;
          applyToStore((store) => ingestOptimisticTurn(store, { turnId, messages }));
          dispatchTurn({ type: 'SUBMIT' });
        },
        accept: (response) => {
          applyToStore((store) =>
            hasIdentityEcho(response)
              ? aliasOptimisticTurn(store, {
                  turnId,
                  backendTurnId: response.turn_id,
                  userMessageIds: response.user_message_ids,
                })
              : acceptOptimisticTurn(store, turnId)
          );
          registryRef.current.accept(turnId, toAcceptedTurn(turnId, response));
          if (isStale()) {
            // Without this the copy just accepted never reconciles away.
            void refetch();
            return;
          }
          // Only now is the turn ours: claiming at submit time would let a
          // cancel raised on another device mid-round-trip read as ours.
          localTurnPendingRef.current = true;
          // Re-key the follow stream so a submit made while it's mid-backoff
          // opens a fresh connection immediately (see `resubscribeToken`). No-op
          // for per-turn mode, which already reopens via `enabled` flipping.
          if (follow) setResubscribeToken((token) => token + 1);
        },
        reject: (error) => {
          const optimistic = ingestedMessages;
          if (optimistic !== null) {
            applyToStore((store) =>
              withdrawOptimisticTurn(store, turnId, optimistic.length)
            );
          }
          registryRef.current.reject(
            turnId,
            error ?? new Error('Turn submission rejected')
          );
          if (isStale()) return;
          if (ingestedMessages !== null) {
            dispatchTurn({ type: 'SUBMIT_REJECTED' });
          }
          // Release the claim, or an unrelated remote cancel would restore our
          // stale prompt.
          localTurnPendingRef.current = false;
        },
      };
    },
    [applyToStore, currentRunIdRef, dispatchTurn, follow, refetch]
  );

  const handleTurnSubmitted = useCallback(
    (messages: Message[], submitOptions?: { turnId?: string }): TurnSubmissionHandle => {
      const handle = beginTurnOperation(submitOptions);
      handle.ingestOptimistic(messages);
      return handle;
    },
    [beginTurnOperation]
  );

  return {
    handleTurnSubmitted,
    beginTurnOperation,
    settleNamedTerminal,
    settleUnnamedTerminal,
    abandonOutstandingOperations,
    resubscribeToken,
    isLocalTurnPending,
    resetTurnTracking,
  };
};
